import * as React from "react";
import { motion } from "framer-motion";
import {
  Building2,
  FileText,
  FolderOpen,
  Bell,
  Search,
  Sparkles,
  Eye,
  RefreshCw,
  ChevronRight,
  Target,
  User,
  Settings,
} from "lucide-react";
import { DashboardGlyph, SubscriptionsGlyph, WealthScopeGlyph } from "./appCustomIcons";

/* ─── Fake sidebar nav — uses the real app's exact icon set ──── */
const NAV_ITEMS = [
  { icon: DashboardGlyph, label: "Dashboard", active: true, chevron: false },
  { icon: WealthScopeGlyph, label: "WealthScope", chevron: true },
  { icon: Building2, label: "Portfolio IQ", chevron: true },
  { icon: Target, label: "DealHunterIQ", chevron: true },
  { icon: FileText, label: "Reports & Data", chevron: false },
  { icon: FolderOpen, label: "Documents", chevron: false },
  { icon: Bell, label: "Alerts & Insights", chevron: false },
  { icon: SubscriptionsGlyph, label: "My Subscriptions", chevron: false },
];

/* ─── Animated counter ────────────────────────────────────────── */
const CountUp: React.FC<{ to: number; prefix?: string; suffix?: string; decimals?: number }> = ({
  to,
  prefix = "",
  suffix = "",
  decimals = 0,
}) => {
  const [val, setVal] = React.useState(0);
  React.useEffect(() => {
    let raf: number;
    const start = performance.now();
    const duration = 1400;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(to * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to]);
  return (
    <span>
      {prefix}
      {val.toFixed(decimals)}
      {suffix}
    </span>
  );
};

const ModuleCard: React.FC<{
  Icon: React.ElementType;
  eyebrow: string;
  title: string;
  gradient: string;
  iconBg: string;
  stats: { label: string; value: React.ReactNode; accent?: string }[];
  desc: string;
  delay: number;
}> = ({ Icon, eyebrow, title, gradient, iconBg, stats, desc, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay }}
    className={`flex-1 min-w-0 rounded-lg p-2.5 ${gradient}`}
  >
    <div className="mb-2 flex items-center justify-between">
      <div className="flex items-center gap-1.5 min-w-0">
        <div className={`w-6 h-6 rounded-md ${iconBg} flex items-center justify-center shrink-0`}>
          <Icon className="w-3 h-3 text-white" />
        </div>
        <div className="min-w-0">
          <p className="text-[6px] font-bold uppercase tracking-wide text-white/60 truncate">{eyebrow}</p>
          <p className="text-[10px] font-bold text-white truncate">{title}</p>
        </div>
      </div>
      <span className="shrink-0 rounded-full bg-white/15 px-1.5 py-0.5 text-[6px] font-semibold text-white">
        Active
      </span>
    </div>
    <div className="mb-2 grid grid-cols-3 gap-1 border-t border-white/10 pt-2">
      {stats.map((s) => (
        <div key={s.label} className="min-w-0">
          <p className="text-[5px] font-semibold uppercase tracking-wide text-white/50 truncate">{s.label}</p>
          <p className={`text-[9px] font-bold truncate ${s.accent ?? "text-white"}`}>{s.value}</p>
        </div>
      ))}
    </div>
    <p className="text-[6px] leading-snug text-white/60 line-clamp-2">{desc}</p>
  </motion.div>
);

/* ─── Dashboard mock screen ──────────────────────────────────── */
export const HeroDashboardMock: React.FC = () => {
  return (
    <div className="relative flex h-full w-full text-[11px] sm:text-xs">
      {/* Sidebar */}
      <div className="flex w-[150px] shrink-0 flex-col gap-0.5 border-r border-gray-200 bg-white px-2 py-3">
        <div className="mb-3 flex items-center gap-1.5 px-1.5">
          <div className="w-5 h-5 rounded bg-blue-600 flex items-center justify-center">
            <span className="text-white text-[9px] font-bold">V</span>
          </div>
          <span className="text-[11px] font-bold text-gray-900">VaWeNa</span>
        </div>
        {NAV_ITEMS.map(({ icon: ItemIcon, label, active, chevron }) => (
          <div
            key={label}
            className={`flex items-center gap-1.5 rounded-md px-2 py-1.5 ${
              active ? "bg-blue-600 text-white" : "text-gray-500"
            }`}
          >
            <ItemIcon className="w-2.5 h-2.5 shrink-0" />
            <span className="flex-1 truncate font-medium text-[8px]">{label}</span>
            {chevron && <ChevronRight className="w-2 h-2 shrink-0 opacity-60" />}
          </div>
        ))}
      </div>

      {/* Main */}
      <div className="flex-1 min-w-0 bg-slate-50 px-2.5 sm:px-4 py-2.5 sm:py-3 overflow-hidden">
        {/* Top bar */}
        <div className="mb-2 flex items-center gap-1.5">
          <div className="flex-1 flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2 py-1 text-gray-400">
            <Search className="w-2.5 h-2.5" />
            <span className="text-[9px]">Search or ask anything...</span>
          </div>
          <div className="flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-1 text-gray-500">
            <Eye className="w-2.5 h-2.5" />
            <span className="text-[8px] font-medium">Hide Details</span>
          </div>
          <div className="relative shrink-0 w-6 h-6 rounded-full border border-gray-200 bg-white flex items-center justify-center">
            <Bell className="w-2.5 h-2.5 text-gray-500" />
            <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-rose-500 text-white text-[6px] font-bold flex items-center justify-center">
              3
            </span>
          </div>
          <div className="flex items-center gap-1 rounded-full bg-blue-600 px-2 py-1 text-white font-semibold shrink-0">
            <Sparkles className="w-2.5 h-2.5" />
            <span className="text-[8px]">AI Assistance</span>
          </div>
          <div className="w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center text-white text-[8px] font-bold shrink-0">
            U
          </div>
        </div>

        {/* Greeting hero banner */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-2 rounded-lg bg-gradient-to-r from-blue-700 to-blue-900 px-3 py-2.5 flex items-center justify-between"
        >
          <div className="min-w-0">
            <p className="text-[6px] font-bold uppercase tracking-wide text-blue-200">
              Friday, September 4
            </p>
            <p className="font-bold text-white text-[11px] sm:text-xs truncate">
              Good morning, User
            </p>
            <p className="text-blue-200 text-[7px] sm:text-[8px] mt-0.5 truncate">
              Here&apos;s your investment intelligence overview
            </p>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <span className="rounded-full bg-white/15 px-2 py-1 text-[7px] font-semibold text-white whitespace-nowrap">
              Complete Bundle
            </span>
            <span className="flex items-center gap-1 text-[6px] text-blue-200">
              <RefreshCw className="w-2 h-2" />
              Updated 1 min ago
            </span>
          </div>
        </motion.div>

        {/* Your Modules */}
        <div className="mb-1.5 flex items-center justify-between">
          <p className="text-[9px] font-bold text-gray-900">Your Modules</p>
          <span className="flex items-center gap-1 text-[7px] font-semibold text-emerald-600">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            All systems live
          </span>
        </div>

        <div className="flex gap-2">
          <ModuleCard
            Icon={WealthScopeGlyph}
            eyebrow="Financial Intelligence"
            title="WealthScope"
            gradient="bg-gradient-to-br from-blue-800 to-blue-950"
            iconBg="bg-white/15"
            stats={[
              { label: "Net Worth", value: <CountUp to={2.4} prefix="$" suffix="M" decimals={1} />, accent: "text-emerald-400" },
              { label: "Total Assets", value: <CountUp to={3.1} prefix="$" suffix="M" decimals={1} /> },
              { label: "Deal Score", value: <CountUp to={92} suffix="/100" />, accent: "text-emerald-400" },
            ]}
            desc="Track net worth, cash flow, buying power and deal readiness in one view."
            delay={0.15}
          />
          <ModuleCard
            Icon={Building2}
            eyebrow="Property Intelligence"
            title="Portfolio IQ"
            gradient="bg-gradient-to-br from-emerald-700 to-emerald-950"
            iconBg="bg-white/15"
            stats={[
              { label: "Properties", value: <CountUp to={12} /> },
              { label: "Portfolio Value", value: <CountUp to={4.8} prefix="$" suffix="M" decimals={1} /> },
              { label: "Avg Score", value: <CountUp to={94} suffix="/100" />, accent: "text-emerald-300" },
            ]}
            desc="Manage properties, track performance, leases, entities and staff in one place."
            delay={0.25}
          />
        </div>

        {/* Signed-in strip */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.35 }}
          className="mt-2 rounded-lg border border-gray-200 bg-white px-3 py-2 flex items-center justify-between gap-2"
        >
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-5 h-5 rounded bg-blue-100 flex items-center justify-center shrink-0">
              <User className="w-2.5 h-2.5 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-[6px] font-bold uppercase tracking-wide text-gray-400">Signed in as</p>
              <p className="text-[8px] font-semibold text-gray-700 truncate">user@gmail.com</p>
            </div>
          </div>
          <span className="flex items-center gap-1 text-[7px] font-semibold text-emerald-600 shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            OAuth · Verified
          </span>
          <span className="inline text-[7px] font-medium text-gray-400 shrink-0">3 modules active</span>
          <span className="flex items-center gap-1 text-[7px] font-semibold text-gray-500 shrink-0">
            <Settings className="w-2.5 h-2.5" />
            <span className="inline">Account Settings</span>
          </span>
        </motion.div>
      </div>

      {/* Floating fake cursor */}
      <motion.div
        className="pointer-events-none absolute z-10 block"
        animate={{
          left: ["58%", "30%", "58%"],
          top: ["28%", "62%", "28%"],
        }}
        transition={{ duration: 6, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" }}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M1 1L14 6.5L8 8.5L6 14.5L1 1Z" fill="white" stroke="#1d4ed8" strokeWidth="1" />
        </svg>
      </motion.div>
    </div>
  );
};

export default HeroDashboardMock;
